import * as React from 'react';

const scripts: { [url: string]: Promise<void> } = {};
const scriptElements: { [url: string]: HTMLScriptElement } = {};

export type Options = {
  callback?: (element: HTMLScriptElement) => HTMLScriptElement;
  async: boolean;
  attributes?: { [key: string]: string };
};

export function loadScript(url: string, options: Options = { async: true }): Promise<void> {
  if (!__BROWSER__) {
    return Promise.resolve();
  }
  if (!(url in scripts)) {
    let element = document.createElement('script');
    element.src = url;
    element.async = options.async;
    if (options?.callback) {
      element = options.callback(element);
    }
    if (options.attributes) {
      for (const [key, value] of Object.entries(options.attributes)) {
        element.setAttribute(key, value);
      }
    }
    scripts[url] = new Promise<void>((resolve, reject) => {
      element.onload = () => {
        scriptElements[url] = element;
        resolve();
      };
      element.onerror = reject;
      document.body.appendChild(element);
    });
  }
  return scripts[url];
}

export function unloadScript(url: string) {
  if (!__BROWSER__) {
    return;
  }
  const element = scriptElements[url];
  if (element) {
    delete scriptElements[url];
    delete scripts[url];
    console.debug('Removing script element for', url);
    document.body.removeChild(element);
  }
}

export function useLoadScript(url: string) {
  if (!__BROWSER__) {
    return false;
  }
  const [isLoaded, setIsLoaded] = React.useState(false);

  React.useEffect(() => {
    loadScript(url)
      .then(() => setIsLoaded(true))
      .catch(() => console.debug(`Asset blocked by client: ${url}`));
    return () => unloadScript(url);
  }, [url]);

  return isLoaded;
}

export function loadScriptOnUserInteraction(url: string, options: Options = { async: true }) {
  if (!__BROWSER__) {
    return Promise.resolve();
  }
  return new Promise<void>((resolve, reject) => {
    const addEvents = () => {
      window.addEventListener('mousemove', load);
      window.addEventListener('touchstart', load);
    };

    const load = () => {
      loadScript(url, options)
        .then(() => {
          window.removeEventListener('touchstart', load);
          window.removeEventListener('mousemove', load);
          window.removeEventListener('load', addEvents);

          resolve();
        })
        .catch(reject);
    };

    window.addEventListener('load', addEvents);
  });
}
